import { useState } from 'react';
import { Loader2, Play, CheckCircle2, AlertTriangle, FileX, Eye } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

function fmtInt(n) {
  if (n == null) return '-';
  return Number(n).toLocaleString('en-GB');
}

const REASON_LABELS = {
  no_xero_id_no_local: 'No Xero link, no local data',
  no_xero_id_has_local: 'No Xero link, has local data',
  xero_id_present_render_failed: 'Xero ID present, render failed',
  not_in_bros: 'Not in BR OS',
  other: 'Other',
};

const REASON_COLORS = {
  no_xero_id_no_local: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
  no_xero_id_has_local: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300',
  xero_id_present_render_failed: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300',
  not_in_bros: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300',
};

const VERDICT_LABELS = {
  unique: 'Unique match',
  multiple: 'Multiple matches',
  none: 'No match',
  link_present_not_backfill: 'Link present',
};

const VERDICT_COLORS = {
  unique: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
  multiple: 'bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300',
  none: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
  link_present_not_backfill: 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300',
};

export function ClientInvoiceReconcilePanel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [dateWindow, setDateWindow] = useState(7);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.clientInvoiceReconcile({
        date_window_days: dateWindow,
      });
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const hasNotFound = result && result.not_found > 0;

  return (
    <Card data-testid="client-invoice-reconcile-panel">
      <CardHeader>
        <CardTitle className="text-lg">Client Invoice Reconcile</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-xs text-muted-foreground">
          Reconciles "Invoice not found" in the Client Invoices view. For each
          not-found invoice, determines whether it is a link gap (backfill
          candidate) or a render/data bug (Xero ID present but lookup fails).
        </p>

        <div className="flex items-end gap-3">
          <div className="space-y-1">
            <Label htmlFor="date-window" className="text-xs">
              Date window (days)
            </Label>
            <Input
              id="date-window"
              type="number"
              min={1}
              max={90}
              value={dateWindow}
              onChange={(e) => setDateWindow(Number(e.target.value) || 7)}
              className="w-24"
              data-testid="date-window-input"
            />
          </div>
          <Button onClick={run} disabled={loading} size="sm" data-testid="run-reconcile-btn">
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Play className="h-4 w-4 mr-2" />
            )}
            Run
          </Button>
        </div>

        {result && (
          <div className="space-y-4" data-testid="reconcile-results">
            {/* Summary tiles */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="summary-tiles">
              <div className="border rounded-lg p-3 text-center">
                <p className="text-xs text-muted-foreground">Total in view</p>
                <p className="text-lg font-semibold tabular-nums" data-testid="tile-total">
                  {fmtInt(result.total_in_view)}
                </p>
              </div>
              <div className="border rounded-lg p-3 text-center">
                <p className="text-xs text-muted-foreground">Viewable</p>
                <p className="text-lg font-semibold tabular-nums text-green-600" data-testid="tile-viewable">
                  {fmtInt(result.viewable)}
                </p>
              </div>
              <div className="border rounded-lg p-3 text-center">
                <p className="text-xs text-muted-foreground">Not found</p>
                <p className="text-lg font-semibold tabular-nums text-red-600" data-testid="tile-not-found">
                  {fmtInt(result.not_found)}
                </p>
              </div>
              <div className="border rounded-lg p-3 text-center">
                <p className="text-xs text-muted-foreground">Probed</p>
                <p className="text-lg font-semibold tabular-nums">
                  {fmtInt(result.probed)}
                  {result.probe_label && (
                    <span className="text-[10px] text-muted-foreground ml-1">
                      ({result.probe_label})
                    </span>
                  )}
                </p>
              </div>
            </div>

            {/* Renderable but view marks not found */}
            {result.renderable_but_view_marks_not_found != null && (
              <div
                className={`flex items-start gap-2 p-3 rounded-lg border text-xs ${
                  result.renderable_but_view_marks_not_found > 0
                    ? 'border-amber-300 bg-amber-50 dark:border-amber-700 dark:bg-amber-950'
                    : 'border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-900'
                }`}
                data-testid="renderable-but-not-found"
              >
                {result.renderable_but_view_marks_not_found > 0
                  ? <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                  : <CheckCircle2 className="h-4 w-4 text-slate-400 mt-0.5 shrink-0" />}
                <div>
                  <p className="font-medium">
                    Renderable but view marks not-found:{' '}
                    <span className="tabular-nums">{fmtInt(result.renderable_but_view_marks_not_found)}</span>
                    {result.probe_label && (
                      <span className="text-muted-foreground font-normal ml-1">
                        (of {result.probe_label})
                      </span>
                    )}
                  </p>
                  {result.renderable_but_view_marks_not_found > 0 && (
                    <p className="text-muted-foreground mt-0.5">
                      These invoices render OK via BR OS but the view's
                      renderability check marks them as not-found. This may
                      indicate a data inconsistency in BR OS.
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Viewable count matches view */}
            {result.viewable_count_matches_view && (
              <div
                className={`flex items-start gap-2 p-3 rounded-lg border text-xs ${
                  result.viewable_count_matches_view.match
                    ? 'border-green-300 bg-green-50 dark:border-green-700 dark:bg-green-950'
                    : 'border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-950'
                }`}
                data-testid="viewable-count-matches-view"
              >
                {result.viewable_count_matches_view.match
                  ? <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400 mt-0.5 shrink-0" />
                  : <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400 mt-0.5 shrink-0" />}
                <div>
                  <p className="font-medium">
                    Viewable count {result.viewable_count_matches_view.match ? 'matches' : 'DOES NOT match'} view
                  </p>
                  <p className="text-muted-foreground">
                    Diagnostic viewable: {fmtInt(result.viewable_count_matches_view.diag_viewable)},
                    View viewable: {fmtInt(result.viewable_count_matches_view.view_viewable)}
                    {result.bros_renderable_check && !result.bros_renderable_check.ok && (
                      <span className="ml-1 text-amber-600">(BR OS check failed)</span>
                    )}
                  </p>
                </div>
              </div>
            )}

            {/* Population validation */}
            {result.population_matches_view && (
              <div
                className={`flex items-start gap-2 p-3 rounded-lg border text-xs ${
                  result.population_matches_view.match
                    ? 'border-green-300 bg-green-50 dark:border-green-700 dark:bg-green-950'
                    : 'border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-950'
                }`}
                data-testid="population-matches-view"
              >
                {result.population_matches_view.match
                  ? <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400 mt-0.5 shrink-0" />
                  : <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400 mt-0.5 shrink-0" />}
                <div>
                  <p className="font-medium">
                    Population {result.population_matches_view.match ? 'matches' : 'DOES NOT match'} view
                  </p>
                  <p className="text-muted-foreground">
                    Diagnostic: {fmtInt(result.population_matches_view.diag_total)},
                    View (count_documents): {fmtInt(result.population_matches_view.view_total)}
                  </p>
                </div>
              </div>
            )}

            {/* Sentinel checks */}
            {result.sentinel_checks && (
              <div className="text-xs space-y-1" data-testid="sentinel-checks">
                {result.sentinel_checks.not_found && (
                  <div data-testid="sentinel-not-found">
                    <p className="font-medium text-muted-foreground">Not-found sentinels:</p>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(result.sentinel_checks.not_found).map(([num, info]) => (
                        <span key={num} className={`font-mono ${
                          info.present && info.classified_not_found
                            ? 'text-green-600'
                            : 'text-red-600'
                        }`}>
                          {info.present && info.classified_not_found ? '\u2713' : '\u2717'} {num}
                          {info.present && !info.classified_not_found && (
                            <span className="text-[9px] ml-0.5">(viewable)</span>
                          )}
                          {!info.present && (
                            <span className="text-[9px] ml-0.5">(absent)</span>
                          )}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                {result.sentinel_checks.viewable && (
                  <div data-testid="sentinel-viewable">
                    <p className="font-medium text-muted-foreground">Viewable sentinels:</p>
                    <div className="flex flex-wrap gap-2">
                      {Object.entries(result.sentinel_checks.viewable).map(([num, info]) => (
                        <span key={num} className={`font-mono ${
                          info.present && info.classified_viewable
                            ? 'text-green-600'
                            : 'text-red-600'
                        }`}>
                          {info.present && info.classified_viewable ? '\u2713' : '\u2717'} {num}
                          {info.present && !info.classified_viewable && (
                            <span className="text-[9px] ml-0.5">(not-found)</span>
                          )}
                          {!info.present && (
                            <span className="text-[9px] ml-0.5">(absent)</span>
                          )}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Excluded counts */}
            {(result.excluded_voided > 0 || result.excluded_ops > 0) && (
              <p className="text-xs text-muted-foreground" data-testid="excluded-counts">
                Excluded: voided {fmtInt(result.excluded_voided)}, ops (INV-S-)
                {' '}{fmtInt(result.excluded_ops)}
              </p>
            )}

            {/* Not-found reason breakdown */}
            {result.reason_breakdown && Object.keys(result.reason_breakdown).length > 0 && (
              <div className="border rounded-lg p-3 space-y-1" data-testid="reason-breakdown">
                <p className="text-sm font-medium">Not-found by reason</p>
                <div className="grid grid-cols-1 gap-1 text-xs">
                  {Object.entries(result.reason_breakdown).map(([reason, count]) => (
                    <div key={reason} className="flex justify-between items-center">
                      <Badge className={`text-[10px] ${REASON_COLORS[reason] || 'bg-slate-100 text-slate-600'}`}>
                        {REASON_LABELS[reason] || reason}
                      </Badge>
                      <span className="tabular-nums font-medium">{fmtInt(count)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Not-found table grouped by reason */}
            {hasNotFound && (
              <div data-testid="not-found-table">
                {Object.entries(
                  groupByReason(result.not_found_detail || [])
                ).map(([reason, items]) => (
                  <details key={reason} className="mb-3" open>
                    <summary className="text-sm font-medium cursor-pointer flex items-center gap-2 mb-2">
                      <Badge className={`text-[10px] ${REASON_COLORS[reason] || 'bg-slate-100 text-slate-600'}`}>
                        {REASON_LABELS[reason] || reason}
                      </Badge>
                      <span className="text-muted-foreground">({items.length})</span>
                    </summary>
                    <div className="overflow-x-auto border rounded-lg">
                      <table className="w-full text-xs">
                        <thead>
                          <tr className="border-b bg-muted/50">
                            <th className="text-left px-2 py-1.5 font-medium">Invoice #</th>
                            <th className="text-left px-2 py-1.5 font-medium">Client</th>
                            <th className="text-right px-2 py-1.5 font-medium">Total</th>
                            <th className="text-left px-2 py-1.5 font-medium">Currency</th>
                            <th className="text-left px-2 py-1.5 font-medium">Status</th>
                            <th className="text-left px-2 py-1.5 font-medium">Date</th>
                            <th className="text-center px-2 py-1.5 font-medium">FA Import</th>
                            <th className="text-left px-2 py-1.5 font-medium">FA Xero ID</th>
                            <th className="text-left px-2 py-1.5 font-medium">Bros Xero ID</th>
                            <th className="text-left px-2 py-1.5 font-medium">Probe</th>
                            {reason.startsWith('no_xero_id') && (
                              <th className="text-left px-2 py-1.5 font-medium">Xero match</th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((d) => (
                            <tr key={d.invoice_id || d.invoice_number} className="border-b last:border-0">
                              <td className="px-2 py-1 font-mono">{d.invoice_number || '-'}</td>
                              <td className="px-2 py-1">{d.client || '-'}</td>
                              <td className="px-2 py-1 text-right tabular-nums">
                                {d.total != null ? d.total.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '-'}
                              </td>
                              <td className="px-2 py-1">{d.currency || '-'}</td>
                              <td className="px-2 py-1">{d.status || '-'}</td>
                              <td className="px-2 py-1">{d.date ? d.date.slice(0, 10) : '-'}</td>
                              <td className="px-2 py-1 text-center">
                                {d.fa_import_present
                                  ? <CheckCircle2 className="h-3 w-3 text-green-600 inline" />
                                  : <FileX className="h-3 w-3 text-red-400 inline" />}
                              </td>
                              <td className="px-2 py-1 font-mono text-[10px]">
                                {d.fa_import_xero_id
                                  ? <span className="text-green-600" title={d.fa_import_xero_id}>{d.fa_import_xero_id.slice(0, 12)}</span>
                                  : <span className="text-muted-foreground/40">-</span>}
                              </td>
                              <td className="px-2 py-1 font-mono text-[10px]">
                                {d.bros_xero_id
                                  ? <span className="text-blue-600" title={d.bros_xero_id}>{d.bros_xero_id.slice(0, 12)}</span>
                                  : <span className="text-muted-foreground/40">-</span>}
                              </td>
                              <td className="px-2 py-1">
                                <ProbeCell probe={d.probe} />
                              </td>
                              {reason.startsWith('no_xero_id') && (
                                <td className="px-2 py-1">
                                  <VerdictCell detail={d} />
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </details>
                ))}
              </div>
            )}

            {!hasNotFound && result.total_in_view > 0 && (
              <div className="flex items-start gap-2 p-3 rounded-lg border border-green-300 bg-green-50 dark:border-green-700 dark:bg-green-950">
                <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5 shrink-0" />
                <p className="text-sm">
                  All {fmtInt(result.viewable)} client invoices in view have a renderable PDF source.
                </p>
              </div>
            )}

            <p className="text-[10px] text-muted-foreground">
              BUILD_COMMIT: {result.build_commit} | Generated: {result.generated_at}
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function groupByReason(details) {
  const groups = {};
  for (const d of details) {
    const r = d.not_found_reason || 'other';
    if (!groups[r]) groups[r] = [];
    groups[r].push(d);
  }
  return groups;
}

function ProbeCell({ probe }) {
  if (!probe) return <span className="text-muted-foreground/40">-</span>;
  if (probe.ok === true) {
    return (
      <span className="text-green-600 text-[10px]">
        OK ({probe.render_source})
      </span>
    );
  }
  if (probe.ok === null) {
    return (
      <span className="text-muted-foreground text-[10px]">
        {probe.error || 'skipped'}
      </span>
    );
  }
  return (
    <span className="text-red-600 text-[10px]" title={probe.error}>
      {probe.http_status || 'err'}: {probe.error?.slice(0, 40) || 'failed'}
    </span>
  );
}

function VerdictCell({ detail }) {
  const verdict = detail.verdict;
  const candidates = detail.xero_candidates;
  if (!verdict) return <span className="text-muted-foreground/40">-</span>;

  return (
    <div className="space-y-0.5">
      <Badge className={`text-[10px] ${VERDICT_COLORS[verdict] || ''}`}>
        {VERDICT_LABELS[verdict] || verdict}
      </Badge>
      {candidates && candidates.length > 0 && (
        <div className="text-[10px] text-muted-foreground">
          {candidates.map((c, i) => (
            <div key={i}>
              {c.xero_invoice_number} ({c.matched_on?.join('+')})
              {c.currency_mismatch && ' [ccy mismatch]'}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
